import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { Eye, Send, Save } from "lucide-react";
import { promptDialog, alertDialog } from "../components/ConfirmDialog";

const SAMPLE_VARS = {
  customer_name: "Sarah Jones",
  customer_first_name: "Sarah",
  attraction_name: "Nightmare Manor",
  event_date: "Saturday 31st October 2026",
  event_time: "8:30 PM – 10:30 PM",
  ticket_type: "General Admission",
  ticket_summary: "2× General Admission, 1× VIP Fast Track",
  order_number: "ST-PREVIEW",
  order_total: "£46.00",
  ticket_link: "#",
  attraction_address: "Lincoln, UK",
  attraction_logo: "",
  // guest list invite
  invitee_name: "Sarah Jones",
  guest_list_title: "Content Creators",
  invite_code: "ABCD1234",
  invite_link: "#",
  // individual ticket move
  guest_name: "Sarah Jones",
  // welcome
  name: "Sarah Jones",
  dashboard_url: "#",
  booking_page_line: "Your booking page is live at scaretickets.co.uk/your-slug",
  support_email: "support@scaretickets.co.uk",
};

const COMMON_VARS = [
  "customer_name", "customer_first_name", "attraction_name", "event_date", "event_time",
  "ticket_type", "ticket_summary", "order_number", "order_total", "ticket_link",
  "attraction_address", "attraction_logo",
];

const VARS_BY_TYPE = {
  guest_list_invite: ["invitee_name", "guest_list_title", "attraction_name", "invite_code", "invite_link", "attraction_address", "attraction_logo"],
  welcome: ["name", "dashboard_url", "booking_page_line", "support_email"],
  ticket_moved: ["guest_name", "attraction_name", "order_number", "ticket_type", "event_date", "event_time", "ticket_link", "attraction_address"],
  ticket_upgraded: ["guest_name", "attraction_name", "order_number", "ticket_type", "previous_ticket_type", "event_date", "event_time", "ticket_link", "attraction_address"],
};

function varsForType(t) {
  return VARS_BY_TYPE[t] || COMMON_VARS;
}

function renderPreview(template, vars) {
  let out = template || "";
  Object.entries(vars).forEach(([k, v]) => { out = out.replaceAll("{{" + k + "}}", v); });
  return out;
}

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ subject: "", html_body: "" });
  const [log, setLog] = useState([]);

  const load = async () => {
    const [t, l] = await Promise.all([api.get("/email-templates"), api.get("/email-log")]);
    setTemplates(t.data);
    setLog(l.data);
    if (t.data.length && !editingId) {
      setEditingId(t.data[0].id);
      setDraft({ subject: t.data[0].subject, html_body: t.data[0].html_body });
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const current = templates.find(t => t.id === editingId);

  const save = async () => {
    await api.patch(`/email-templates/${editingId}`, draft);
    load();
  };
  const test = async () => {
    const to = await promptDialog({
      title: "Send test email",
      description: "Where should we send this preview?",
      promptLabel: "Recipient email", promptType: "email",
      confirmLabel: "Send test",
    });
    if (!to) return;
    await api.post(`/email-templates/${editingId}/test`, { to });
    await alertDialog({
      title: "Test email queued",
      description: "Check the email log below to see the delivery status.",
    });
    load();
  };

  return (
    <DashboardLayout title="Email templates" subtitle="Customise transactional emails sent to customers.">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        <div className="st-card p-3">
          {templates.map((t) => (
            <button key={t.id} onClick={() => { setEditingId(t.id); setDraft({ subject: t.subject, html_body: t.html_body }); }}
                    data-testid={`tpl-${t.template_type}`}
                    className={`w-full text-left px-3 py-3 rounded-lg ${editingId === t.id ? "bg-[#dc2626] text-white" : "hover:bg-[#161616]"}`}>
              <div className="font-semibold text-sm">{labelFor(t.template_type)}</div>
              <div className="text-xs opacity-80 mt-0.5">{t.template_type}</div>
            </button>
          ))}
        </div>

        {current && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="st-card p-4">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Editor</h3>
              <label className="text-xs text-[#8a8a8a]">Subject</label>
              <input className="st-input mt-1" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="tpl-subject" />
              <label className="text-xs text-[#8a8a8a] mt-3 block">HTML body</label>
              <textarea className="st-input mt-1 min-h-[320px] st-mono text-xs" value={draft.html_body}
                        onChange={(e) => setDraft({ ...draft, html_body: e.target.value })} data-testid="tpl-body" />
              <div className="flex gap-2 mt-4">
                <button className="st-btn" onClick={save} data-testid="tpl-save"><Save size={14}/> Save</button>
                <button className="st-btn st-btn-ghost" onClick={test} data-testid="tpl-test"><Send size={14}/> Send test</button>
              </div>
              <div className="mt-3 text-xs text-[#8a8a8a]">
                Variables: {varsForType(current.template_type).map(k => <code key={k} className="mx-1 text-[#dc2626]">{"{{"+k+"}}"}</code>)}
              </div>
            </div>

            <div className="st-card p-4">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3 flex items-center gap-2"><Eye size={14}/> Preview</h3>
              <div className="text-xs text-[#8a8a8a] mb-2">Subject: <span className="text-white">{renderPreview(draft.subject, SAMPLE_VARS)}</span></div>
              <div className="rounded-lg overflow-hidden border border-[#1a1a1a]"
                   dangerouslySetInnerHTML={{ __html: renderPreview(draft.html_body, SAMPLE_VARS) }} />
            </div>
          </div>
        )}
      </div>

      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mt-10 mb-3">Email log</h3>
      <div className="st-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
            <tr><th className="px-4 py-3">When</th><th>To</th><th>Subject</th><th>Type</th><th>Status</th></tr>
          </thead>
          <tbody data-testid="email-log-tbody">
            {log.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[#8a8a8a]">No emails yet.</td></tr>}
            {log.map(e => (
              <tr key={e.id} className="border-t border-[#1a1a1a]">
                <td className="px-4 py-2.5 text-[#bbb]">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td>{e.to}</td><td className="truncate max-w-[300px]">{e.subject}</td>
                <td className="text-xs text-[#8a8a8a]">{e.template_type}</td>
                <td><span className={`st-chip ${e.status === "sent" ? "st-chip-green" : e.status === "failed" ? "st-chip-red" : ""}`}>{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

function labelFor(t) {
  return ({
    order_confirmation: "Order confirmation",
    ticket_delivery: "Ticket delivery",
    day_before_reminder: "Day-before reminder",
    post_visit_review: "Post-visit review",
    cancellation: "Cancellation",
    guest_list_invite: "Guest list invite",
    guest_list_ticket: "Guest list tickets",
    welcome: "Welcome",
    partial_refund: "Partial refund",
    payment_link: "Payment link",
    booking_moved: "Booking moved",
    ticket_upgraded: "Ticket upgraded",
    ticket_moved: "Individual ticket moved",
    order_updated: "Order updated",
  })[t] || t;
}
